<template>
  <div class="booking-page">
    <!-- Banner Section -->
    <Banner
      image="https://images.unsplash.com/photo-1583907659441-addbe699e921?q=80&w=2070"
      title="Book Tickets"
      height="700px"
    />

    <!-- Wavy Divider -->
    <div class="wave-container">
      <svg class="wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <path class="wave-path" d="M0,96L40,106.7C80,117,160,139,240,154.7C320,171,400,181,480,165.3C560,149,640,107,720,90.7C800,75,880,85,960,112C1040,139,1120,181,1200,181.3C1280,181,1360,139,1400,117.3L1440,96L1440,400L1400,400C1360,400,1280,400,1200,400C1120,400,1040,400,960,400C880,400,800,400,720,400C640,400,560,400,480,400C400,400,320,400,240,400C160,400,80,400,40,400L0,400Z"></path>
      </svg>
    </div>

    <!-- Booking Content -->
    <div class="container">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Loading...
      </div>
      <div v-else-if="error" class="error-state">
        <i class="fas fa-exclamation-circle"></i>
        <p>{{ error }}</p>
        <button @click="fetchTicketTypes" class="retry-btn">
          <i class="fas fa-redo"></i> Retry
        </button>
      </div>
      <div v-else class="booking-content">
        <!-- Date Selection -->
        <DatePicker @update:model-value="handleDateSelect" :availableDates="dates" />
        <div class="date-selection">
          <h3>Select Visit Date</h3>
          <input 
            type="date" 
            v-model="bookingDate"
            :min="minDate"
            class="date-input"
          >
        </div>

        <!-- Ticket Selection -->
        <div class="tickets-container">
          <div v-for="ticket in ticketTypes" :key="ticket.id" class="ticket-card">
            <div class="ticket-info">
              <h3>{{ settingStore.language === 'en' ? ticket.name_en : ticket.name_ar }}</h3>
              <p class="price">SAR {{ ticket.price }}</p>
              <p class="description">{{ ticket.description }}</p>
            </div>
            <div class="quantity-selector">
              <button 
                @click="updateQuantity(ticket.id, ticket.quantity - 1)"
                :disabled="ticket.quantity <= 0"
                class="quantity-btn"
              >
                <i class="fas fa-minus"></i>
              </button>
              <span class="quantity">{{ ticket.quantity }}</span>
              <button 
                @click="updateQuantity(ticket.id, ticket.quantity + 1)"
                class="quantity-btn"
              >
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Booking Summary -->
        <div class="booking-summary">
          <h3>Booking Summary</h3>
          <div class="summary-items">
            <div v-for="ticket in selectedTickets" :key="ticket.id" class="summary-item">
              <span>{{ settingStore.language === 'en' ? ticket.name_en : ticket.name_ar }} x{{ ticket.quantity }}</span>
              <span>SAR {{ ticket.price * ticket.quantity }}</span>
            </div>
          </div>
          <div class="total">
            <span>Total Amount</span>
            <span>SAR {{ totalAmount }}</span>
          </div>
        </div>

        <!-- Checkout Button -->
        <button 
          @click="proceedToCheckout"
          :disabled="!isValidBooking"
          class="checkout-btn"
        >
          Proceed to Checkout
        </button>

        <!-- Terms and Conditions -->
        <p class="terms">
          By proceeding with the booking, you agree to our 
          <router-link to="/terms-and-condition">terms and conditions</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import Banner from "@/components/Banner.vue";
import axiosInstance from '@/plugins/axios';
import { useSettingStore } from '@/stores/settingStore';
import { useRouter } from 'vue-router';
import DatePicker from '@/components/DatePicker.vue';

const settingStore = useSettingStore();
const router = useRouter();
const dates = ref([]);

const getDates = async () => {
  const response = await axiosInstance.get('api/calendar/limit');
  if(response.data?.success == true){
    dates.value = response.data?.data?.specialMonthDates;
    loading.value = false;
  }
}
const handleDateSelect = async (date) => {
  const selectedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
  await axiosInstance.get('api/tickets/list/' + selectedDate);
};
// Reactive state
const ticketTypes = ref([
  // Fallback data in case API fails
  {
    id: 1,
    name_en: "Full Day Ticket",
    name_ar: "تذكرة اليوم الكامل",
    price: 150,
    description: "Access to all park attractions all day",
    quantity: 0
  },
  {
    id: 2,
    name_en: "Evening Ticket",
    name_ar: "تذكرة المساء",
    price: 100,
    description: "Evening access to selected attractions",
    quantity: 0
  }
]);
const bookingDate = ref('');
const loading = ref(true);
const error = ref(null);

// Computed properties
const minDate = computed(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});

const selectedTickets = computed(() => {
  return ticketTypes.value.filter(ticket => ticket.quantity > 0);
});

const totalAmount = computed(() => {
  return selectedTickets.value.reduce((total, ticket) => 
    total + (ticket.price * ticket.quantity), 0
  );
});

const isValidBooking = computed(() => {
  return bookingDate.value && totalAmount.value > 0;
});

// Methods
const fetchTicketTypes = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    const response = await axiosInstance.get('api/ticket-categories');
    
    // Log the full response for debugging
    console.log('Ticket Categories Response:', response);
    console.log('Response Data:', response.data);
    
    // Validate response
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error('Invalid response format');
    }

    // Add price, description, and quantity to ticket types
    ticketTypes.value = response.data.map(ticket => ({
      ...ticket,
      price: ticket.id === 1 ? 150 : 100, // Example pricing logic
      description: ticket.id === 1 
        ? 'Access to all park attractions all day' 
        : 'Evening access to selected attractions',
      quantity: 0
    }));
  } catch (err) {
    console.error('Error fetching ticket types:', err);
    
    // More detailed error handling
    if (err.response) {
      // The request was made and the server responded with a status code
      error.value = `Server Error: ${err.response.status} - ${err.response.data.message || 'Unable to fetch ticket types'}`;
    } else if (err.request) {
      // The request was made but no response was received
      error.value = 'Network Error: No response received from server';
    } else {
      // Something happened in setting up the request
      error.value = `Error: ${err.message}`;
    }
  } finally {
    loading.value = false;
  }
};

const updateQuantity = (ticketId, newQuantity) => {
  const ticket = ticketTypes.value.find(t => t.id === ticketId);
  if (ticket) {
    ticket.quantity = Math.max(0, newQuantity);
  }
};

const proceedToCheckout = () => {
  if (isValidBooking.value) {
    // Store booking details in local storage for success page
    const bookingDetails = {
      date: bookingDate.value,
      tickets: ticketTypes.value,
      total: totalAmount.value
    };
    localStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
    
    // Navigate to checkout or transaction page
    router.push('/transaction_success');
  }
};

// Lifecycle hook
onMounted(() => {
  // fetchTicketTypes();
  getDates();
});
</script>

<style scoped>


/* Styles remain the same as before */
.booking-page {
  position: relative;
  background: white;
  margin-top: 700px;
}

.wave-container {
  position: absolute;
  top: -200px;
  left: 0;
  width: 100%;
  height: 220px;
  overflow: hidden;
}

.wave-top {
  position: absolute;
  bottom: -20px;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: bottom;
  animation: waveFloat 5s ease-in-out infinite alternate;
}

.wave-path {
  fill: #ffffff;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1));
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.booking-content {
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 30px;
}

.date-selection {
  margin-bottom: 30px;
}

.date-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
}

.tickets-container {
  display: grid;
  gap: 20px;
  margin-bottom: 30px;
}

.ticket-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ticket-info h3 {
  margin: 0;
  color: #1268B1;
}

.price {
  font-size: 1.2em;
  font-weight: bold;
  color: #333;
  margin: 5px 0;
}

.description {
  color: #666;
  font-size: 0.9em;
}

.quantity-selector {
  display: flex;
  align-items: center;
  gap: 15px;
}

.quantity-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #1268B1;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
}

.quantity-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.quantity {
  font-size: 1.2em;
  font-weight: bold;
  min-width: 30px;
  text-align: center;
}

.booking-summary {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
}

.summary-items {
  margin: 15px 0;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
  color: #666;
}

.total {
  border-top: 2px solid #ddd;
  margin-top: 15px;
  padding-top: 15px;
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 1.2em;
}

.checkout-btn {
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 8px;
  background: #1268B1;
  color: white;
  font-size: 1.1em;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
}

.checkout-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.checkout-btn:not(:disabled):hover {
  background: #0d4f8c;
}

.terms {
  text-align: center;
  margin-top: 20px;
  color: #666;
  font-size: 0.9em;
}

.terms a {
  color: #1268B1;
  text-decoration: none;
}


.loading-state, .error-state {
  text-align: center;
  padding: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.error-state {
  color: #dc3545;
}

.retry-btn {
  background: #1268B1;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.retry-btn:hover {
  background: #0d4f8c;
}

.error-state i {
  font-size: 3rem;
  margin-bottom: 15px;
}

@keyframes waveFloat {
  0% {
    transform: translateY(0) scaleY(1.02);
  }
  100% {
    transform: translateY(-10px) scaleY(1.05);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .container {
    padding: 20px;
  }

  .booking-content {
    padding: 20px;
  }

  .ticket-card {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }

  .wave-container {
    top: -150px;
    height: 170px;
  }
}

@media (max-width: 480px) {
  .wave-container {
    top: -100px;
    height: 120px;
  }
}

/* RTL Support */
:dir(rtl) .wave-top {
  transform: scaleX(-1);
}
</style>